// Gate do painel admin da Trilha: papéis derivados dos escopos do JWT
// emitido pelo core-schief-ai — quem carrega qual escopo é definido no core
// (pelo CTO), não no front. Mesmo modelo de Security scopes dos backends.
//
// Dois papéis, e os DOIS já existem na plataforma — nenhum escopo novo:
//
//   master — backoffice da licito.guru. É o escopo `admin` do core, o mesmo
//            que protege a API /admin dele (organizações, assinaturas,
//            planos, papéis). Quem o carrega administra a PLATAFORMA. Viaja
//            no JWT, então dá para ler aqui.
//
//   org    — gestor da organização cliente. No core NÃO é papel nem escopo:
//            é a coluna booleana `users.org_admin`, a mesma que decide quem
//            distribui as cotas do plano entre os usuários. Ela não viaja no
//            token — chega pelo `userInfo` do contexto de auth (o core a
//            devolve em GET /users), e o academy-service a confirma por sua
//            conta antes de entregar qualquer dado.
//
// Este arquivo decide o que a TELA mostra. O que o DADO alcança é decidido
// no servidor: org-scope pelo organization_id do token, e o painel da org só
// abre depois que o academy confirma o org_admin com o core.
import {decodeToken} from 'react-jwt'
import {CookiesService} from '../../services/Cookie.service'

export type PapelAdmin = 'master' | 'org' | null

// A MESMA string vive no constants.py do academy (ESCOPO_MASTER). É o escopo
// que o core já emite — não se inventa papel novo para dizer o que `admin`
// já diz.
export const ESCOPO_MASTER = 'admin'

interface PayloadToken {
    scopes?: string[]
}

export function papelAdmin(orgAdmin = false): PapelAdmin {
    // Liberação TEMPORÁRIA para desenvolvimento local, só em build DEV (em
    // produção o bundler elimina o ramo por dead code):
    //   VITE_TRILHA_ADMIN_LIBERADO=true  -> master (retrocompatível)
    //   VITE_TRILHA_ADMIN_LIBERADO=org   -> simula o admin de organização,
    //                                       para testar a visão só-leitura
    if (import.meta.env.DEV) {
        // Atalho de URL para alternar as duas visões sem reiniciar o Vite
        // (variável de ambiente é lida uma vez, no boot do servidor):
        //   /trilha-guru/admin?papel=org     -> visão do admin da organização
        //   /trilha-guru/admin?papel=master  -> visão do backoffice
        // Vence a variável de ambiente porque é o mais específico dos dois.
        try {
            const forcado = new URLSearchParams(window.location.search).get(
                'papel'
            )
            if (forcado === 'master' || forcado === 'org') return forcado
        } catch {
            /* sem window (SSR/teste): segue para a variável de ambiente */
        }
        const flag = import.meta.env.VITE_TRILHA_ADMIN_LIBERADO
        if (flag === 'true') return 'master'
        if (flag === 'org') return 'org'
    }
    try {
        const authData = new CookiesService().getAuthData()
        if (!authData?.access_token) return null
        const payload = decodeToken<PayloadToken>(authData.access_token)
        const scopes = payload?.scopes ?? []
        // Master primeiro: quem administra a plataforma também é gestor de
        // qualquer org, e mostrar-lhe a visão reduzida seria tirar acesso.
        if (scopes.includes(ESCOPO_MASTER)) return 'master'
        return orgAdmin ? 'org' : null
    } catch {
        return null
    }
}

// Os dois papéis entram no painel; o que muda lá dentro é o que cada um vê.
export function usuarioTemEscopoAdmin(orgAdmin = false): boolean {
    return papelAdmin(orgAdmin) !== null
}
